import { runInAction, when } from "mobx";
import { LAUNCHDARKLY_ENABLED } from "@/config";
import { PERSISTED_TABLE_PREFS_FLAG } from "@/lib/launchdarkly/flags";

/**
 * The `persisted-table-prefs` gate (NG-517), for code that is not a React
 * component: the ProjectStore, IntakeStore and ReportStore persistence
 * reactions, which reach `readTablePref` / `writeTablePref` directly rather than
 * through the hooks, and the report route's `clientLoader`.
 *
 * Split from `use-table-prefs-gate.ts` to break a dependency CYCLE, not for
 * tidiness: that module reaches `useStore()`, so a store importing it closed the
 * loop stores → gate → store-context → root.store → stores, which
 * `check-deps.sh` blocks. This half imports nothing from the store layer, so
 * both sides can depend on it.
 */

/**
 * How long a caller will wait for LaunchDarkly before proceeding without it.
 *
 * The wait exists so a restored sort is applied before rows are painted rather
 * than a beat after.
 *
 * The CAP is insurance, not a fix for a known bug — worth stating precisely,
 * because `FeatureFlagStore` registers no `failed` handler and it is tempting to
 * conclude a broken LaunchDarkly hangs forever. It does not: the SDK emits
 * `ready` on failure as well as success (`InitializationState.js` —
 * `signalFailure` emits both `failed` and `ready`), so an ad-blocked, offline or
 * rejected client still resolves, flags fall back to off, and every consumer
 * carries on. What the cap actually covers is the residual case neither event
 * reaches: a request that never settles at all. That is bounded by the browser's
 * own network timeout rather than being infinite, so the cap turns a possible
 * multi-second stall into a 1s one.
 *
 * 1s because that is the Miller/Nielsen flow-break threshold this repo budgets
 * reads against (`/response-time-limits`), and the hold gates the next action.
 * The realistic paths are all far under it: LaunchDarkly is disabled outright in
 * local dev and the hermetic CI/e2e stack, and a returning user's flags are
 * bootstrapped from localStorage, so `ready` fires on the first tick. Only a
 * genuine first visit waits on the network, and only until this cap.
 */
export const FLAG_WAIT_CAP_MS = 1000;

/** Whether the two gated slices (`sorting`, `columnFilters`) may persist.
 *
 *  Takes the flag store rather than the root store so callers stay easy to
 *  construct in tests, and returns a plain boolean: a store has no paint to
 *  hold, so it has no use for the hook's `pending` — an unresolved flag is
 *  simply not enabled yet. */
export function tablePrefsEnabled(featureFlags: {
  isReady: boolean;
  isEnabled: (key: string, fallback?: boolean) => boolean;
}): boolean {
  return featureFlags.isReady && featureFlags.isEnabled(PERSISTED_TABLE_PREFS_FLAG, false);
}

/**
 * Resolve once the gate has an answer — for a `clientLoader`, which runs before
 * render and so cannot hold on the hook's `pending`.
 *
 * Only `report-detail.tsx` needs this. The projects route re-hydrates from its
 * list's seed effect when the flag lands, which is cheaper because it does not
 * delay navigation; a report has no equivalent re-entry point, and its snapshot
 * is sorted SERVER-side, so a sort restored after the fetch would mean loading
 * the page twice.
 *
 * Already-resolved is the overwhelmingly common case — LaunchDarkly is disabled
 * outright in local dev and the hermetic stack, and bootstraps from localStorage
 * otherwise — and costs one microtask. A cold deep link waits, bounded by
 * {@link FLAG_WAIT_CAP_MS}.
 */
export function whenTablePrefsGateReady(featureFlags: { isReady: boolean }): Promise<void> {
  if (!LAUNCHDARKLY_ENABLED || tablePrefsGateResolved(featureFlags)) return Promise.resolve();
  const ready = when(() => featureFlags.isReady);
  // `when()`'s promise REJECTS when cancelled, and the timeout branch always
  // cancels it — swallow that so a capped wait is not an unhandled rejection.
  const settled = ready.catch(() => undefined);
  return Promise.race([
    settled,
    new Promise<void>((resolve) => setTimeout(resolve, FLAG_WAIT_CAP_MS)),
  ]).then(() => {
    ready.cancel();
  });
}

/**
 * `tablePrefsEnabled`, read from code that is neither a reaction nor a React
 * render — a route `clientLoader`, or a store method one of them calls.
 *
 * The `runInAction` is not there to write anything. MobX allows an untracked
 * observable read **only** inside an action or a reaction (both call
 * `allowStateReadsStart(true)`); anywhere else `observableRequiresReaction`
 * fires "Observable ... being read outside a reactive context", which this repo
 * is driving to zero and which `skills.test.tsx` asserts on directly.
 *
 * It is deliberately NOT folded into `tablePrefsEnabled` itself: that one is
 * read inside `useTablePrefsGate` during an `observer` render, where the read
 * MUST stay tracked so a flag arriving late re-renders the consumer. Wrapping it
 * there would silently make the gate non-reactive.
 */
export function tablePrefsEnabledNow(featureFlags: {
  isReady: boolean;
  isEnabled: (key: string, fallback?: boolean) => boolean;
}): boolean {
  return runInAction(() => tablePrefsEnabled(featureFlags));
}

/** `isReady` read the same non-reactive way — see {@link tablePrefsEnabledNow}. */
function tablePrefsGateResolved(featureFlags: { isReady: boolean }): boolean {
  return runInAction(() => featureFlags.isReady);
}
